/**
 * Workspace Whiteboard — multi-task whiteboard for the interior scene.
 * Extends the OnboardingWhiteboard canvas-texture approach but supports
 * multiple tasks with active/done sections + brainstorming mode.
 *
 * Layout (task mode):
 *   Header: "TASKS" (grey)
 *   Active: Blue bullets + task text for in_progress tasks
 *   Done:   Green checks + strikethrough for completed tasks (last 3)
 *
 * Layout (brainstorming mode):
 *   Centered "BRAINSTORMING" header + large centered text
 */

import * as THREE from 'three';
import type { TaskRecord } from '@/data/types';

const CANVAS_W = 1024;
const CANVAS_H = 680;
const S = 2; // Scale factor (all coords designed at 512x340, scaled 2x)
const MAX_DONE_SHOWN = 3;

export class WorkspaceWhiteboard {
  private scene: THREE.Scene;
  private surface: THREE.Mesh | null = null;
  private originalMaterial: THREE.Material | null = null;
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private texture: THREE.CanvasTexture | null = null;
  private currentTasks: TaskRecord[] = [];
  private isBrainstorming = false;

  constructor(scene: THREE.Scene) {
    this.scene = scene;
    this.canvas = document.createElement('canvas');
    this.canvas.width = CANVAS_W;
    this.canvas.height = CANVAS_H;
    this.ctx = this.canvas.getContext('2d')!;

    // Find whiteboard surface mesh
    const wb = scene.getObjectByName('whiteboard') as THREE.Group | undefined;
    if (wb && wb.children.length >= 2) {
      this.surface = wb.children[1] as THREE.Mesh;
      this.originalMaterial = this.surface.material as THREE.Material;
    }
  }

  /**
   * Full redraw from a task list. Called whenever task state changes.
   */
  syncTasks(tasks: TaskRecord[]): void {
    this.currentTasks = tasks;
    if (!this.isBrainstorming) {
      this.redraw();
    }
  }

  // ── Brainstorming Mode ──────────────────────────────────────

  /**
   * Show brainstorming text on the whiteboard (replaces task view).
   * Used to mask AI latency while Alex thinks.
   */
  showBrainstorming(text?: string): void {
    this.isBrainstorming = true;
    this.drawBrainstorming(text || 'Brainstorming new task...');
  }

  /**
   * Clear brainstorming mode and revert to task view.
   */
  clearBrainstorming(): void {
    this.isBrainstorming = false;
    this.redraw();
  }

  private drawBrainstorming(text: string): void {
    if (!this.surface) return;
    const ctx = this.ctx;

    // White background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);

    // Header: "BRAINSTORMING" centered
    ctx.fillStyle = '#9ca3af';
    ctx.font = `bold ${22 * S}px system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.fillText('BRAINSTORMING', CANVAS_W / 2, 50 * S);

    // Divider
    ctx.strokeStyle = '#e5e7eb';
    ctx.lineWidth = S;
    ctx.beginPath();
    ctx.moveTo(60 * S, 62 * S);
    ctx.lineTo(CANVAS_W - 60 * S, 62 * S);
    ctx.stroke();

    // Main text — large indigo, centered vertically
    ctx.fillStyle = '#6366f1';
    ctx.font = `bold ${20 * S}px system-ui, sans-serif`;
    ctx.fillText(text, CANVAS_W / 2, CANVAS_H / 2);

    // Decorative dots (thinking indicator)
    ctx.fillStyle = '#a5b4fc';
    ctx.font = `${16 * S}px system-ui, sans-serif`;
    ctx.fillText('\u2022  \u2022  \u2022', CANVAS_W / 2, CANVAS_H / 2 + 40 * S);

    // Reset text alignment
    ctx.textAlign = 'left';

    this.applyTexture();
  }

  // ── Task Drawing ────────────────────────────────────────────

  private redraw(): void {
    if (!this.surface) return;

    const ctx = this.ctx;

    // White background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);

    // Header
    ctx.fillStyle = '#999999';
    ctx.font = `bold ${18 * S}px system-ui, sans-serif`;
    ctx.fillText('TASKS', 30 * S, 40 * S);

    // Divider
    ctx.strokeStyle = '#cccccc';
    ctx.lineWidth = S;
    ctx.beginPath();
    ctx.moveTo(30 * S, 52 * S);
    ctx.lineTo(CANVAS_W - 30 * S, 52 * S);
    ctx.stroke();

    let y = 78 * S;

    // Active tasks (pending + in_progress)
    const active = this.currentTasks.filter(t => t.status === 'pending' || t.status === 'in_progress');
    for (const task of active) {
      if (y > CANVAS_H - 30 * S) break;
      y = this.drawTaskRow(ctx, task, y);
    }

    // Completed tasks (last N)
    const done = this.currentTasks
      .filter(t => t.status === 'completed')
      .sort((a, b) => (b.completed_at || 0) - (a.completed_at || 0))
      .slice(0, MAX_DONE_SHOWN);

    if (done.length > 0 && y < CANVAS_H - 40 * S) {
      y += 6 * S;
      ctx.strokeStyle = '#e5e7eb';
      ctx.beginPath();
      ctx.moveTo(30 * S, y);
      ctx.lineTo(CANVAS_W - 30 * S, y);
      ctx.stroke();
      y += 16 * S;

      for (const task of done) {
        if (y > CANVAS_H - 20 * S) break;
        y = this.drawDoneRow(ctx, task, y);
      }
    }

    // Failed tasks
    const failed = this.currentTasks.filter(t => t.status === 'failed');
    for (const task of failed) {
      if (y > CANVAS_H - 20 * S) break;
      y = this.drawFailedRow(ctx, task, y);
    }

    // Show "no tasks" if empty
    if (this.currentTasks.length === 0) {
      ctx.fillStyle = '#9ca3af';
      ctx.font = `${16 * S}px system-ui, sans-serif`;
      ctx.fillText('No active tasks', 30 * S, 80 * S);
    }

    this.applyTexture();
  }

  private drawTaskRow(ctx: CanvasRenderingContext2D, task: TaskRecord, y: number): number {
    const isActive = task.status === 'in_progress';

    // Bullet
    ctx.fillStyle = isActive ? '#6366f1' : '#93c5fd';
    ctx.beginPath();
    ctx.arc(44 * S, y, 5 * S, 0, Math.PI * 2);
    ctx.fill();

    // Spinning indicator for in_progress
    if (isActive) {
      ctx.fillStyle = '#818cf8';
      ctx.beginPath();
      ctx.arc(44 * S + 8 * S, y, 2 * S, 0, Math.PI * 2);
      ctx.fill();
    }

    // Task text
    ctx.fillStyle = '#1a1a2e';
    ctx.font = isActive ? `bold ${16 * S}px system-ui, sans-serif` : `${16 * S}px system-ui, sans-serif`;

    const text = this.truncateText(ctx, task.description, CANVAS_W - 100 * S);
    ctx.fillText(text, 58 * S, y + 5 * S);

    return y + 28 * S;
  }

  private drawDoneRow(ctx: CanvasRenderingContext2D, task: TaskRecord, y: number): number {
    ctx.strokeStyle = '#22c55e';
    ctx.lineWidth = 2 * S;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(38 * S, y);
    ctx.lineTo(42 * S, y + 4 * S);
    ctx.lineTo(50 * S, y - 4 * S);
    ctx.stroke();

    ctx.fillStyle = '#9ca3af';
    ctx.font = `${14 * S}px system-ui, sans-serif`;
    const text = this.truncateText(ctx, task.description, CANVAS_W - 100 * S);
    ctx.fillText(text, 58 * S, y + 4 * S);

    const textWidth = ctx.measureText(text).width;
    ctx.strokeStyle = '#d1d5db';
    ctx.lineWidth = S;
    ctx.beginPath();
    ctx.moveTo(58 * S, y + S);
    ctx.lineTo(58 * S + textWidth, y + S);
    ctx.stroke();

    return y + 24 * S;
  }

  private drawFailedRow(ctx: CanvasRenderingContext2D, task: TaskRecord, y: number): number {
    ctx.strokeStyle = '#ef4444';
    ctx.lineWidth = 2 * S;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(39 * S, y - 4 * S);
    ctx.lineTo(49 * S, y + 4 * S);
    ctx.moveTo(49 * S, y - 4 * S);
    ctx.lineTo(39 * S, y + 4 * S);
    ctx.stroke();

    ctx.fillStyle = '#f87171';
    ctx.font = `${14 * S}px system-ui, sans-serif`;
    const text = this.truncateText(ctx, task.description, CANVAS_W - 100 * S);
    ctx.fillText(text, 58 * S, y + 4 * S);

    return y + 24 * S;
  }

  private truncateText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string {
    if (ctx.measureText(text).width <= maxWidth) return text;
    let truncated = text;
    while (truncated.length > 0 && ctx.measureText(truncated + '...').width > maxWidth) {
      truncated = truncated.slice(0, -1);
    }
    return truncated + '...';
  }

  private applyTexture(): void {
    if (!this.surface) return;

    if (this.texture) {
      this.texture.needsUpdate = true;
    } else {
      this.texture = new THREE.CanvasTexture(this.canvas);
      this.texture.colorSpace = THREE.SRGBColorSpace;
      const mat = new THREE.MeshStandardMaterial({
        map: this.texture,
        roughness: 0.3,
      });
      this.surface.material = mat;
    }
  }

  clear(): void {
    if (!this.surface || !this.originalMaterial) return;
    this.surface.material = this.originalMaterial;
    this.currentTasks = [];
    this.isBrainstorming = false;

    if (this.texture) {
      this.texture.dispose();
      this.texture = null;
    }
  }

  dispose(): void {
    this.clear();
  }
}
